import { ICustomReportListRequestPayload } from '@/Interfaces/ReportBuilder/RequestPayload.interface';
import { IScheduledCustomReport } from '@/Interfaces/ReportBuilder/ScheduledCustomReport.interface';
import { Grid } from '@/components/Grid';
import ServerSidePagination from '@/components/Grid/Pagination/ServerSidePagination';
import { EXPAND_OPTIONS } from '@/constants/grid';
import {
  CustomReportCancelScheduledFunction,
  CustomReportEditScheduledFunction,
} from '@/types/ReportBuilder/GridFunction.types';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { GridPaginationModel, GridSortModel, gridClasses } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import GridToolBar from '../Components/GridToolbar';
import { getColumns } from './columns';

interface IProps {
  data: IScheduledCustomReport[];
  loading?: boolean;
  rowCount: number;
  loadScheduledReports: (payload?: ICustomReportListRequestPayload) => void;
  onCancelScheduledReport: CustomReportCancelScheduledFunction;
  onEditScheduledReport: CustomReportEditScheduledFunction;
}

const ScheduledReportsGrid = ({
  data,
  loading,
  rowCount,
  onCancelScheduledReport,
  onEditScheduledReport,
  loadScheduledReports,
}: IProps) => {
  //#region sort and pagination
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 15,
  });

  const handlePaginationModelChange = useCallback((newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
  }, []);

  const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  }, []);

  useEffect(() => {
    loadScheduledReports({ page: paginationModel.page + 1, pageSize: paginationModel.pageSize, sort: sortModel });
  }, [loadScheduledReports, paginationModel, sortModel]);
  //#endregion

  const columns = useMemo(
    () => getColumns(onCancelScheduledReport, onEditScheduledReport),
    [onCancelScheduledReport, onEditScheduledReport],
  );

  return (
    <>
      <Grid
        sx={{
          border: 'none',
          [`& .${gridClasses.main}`]: {
            borderBottom: '1px solid rgb(227, 227, 227)',
          },
          [`& .${gridClasses.columnHeaders}`]: {
            borderRadius: 0,
            borderTop: '1px solid rgb(227, 227, 227)',
          },
        }}
        columns={columns}
        loading={loading}
        autosizeOnMount
        autosizeOptions={EXPAND_OPTIONS}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        showCellVerticalBorder
        showColumnVerticalBorder
        rows={data}
        columnHeaderHeight={30}
        sortingMode='server'
        paginationMode='server'
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        pagination
        slots={{
          pagination: ServerSidePagination,
          toolbar: GridToolBar,
          columnSortedAscendingIcon: KeyboardArrowUp,
          columnSortedDescendingIcon: KeyboardArrowDown,
        }}
        slotProps={{
          pagination: {
            // @ts-expect-error loading prop
            loading,
            rowCount,
          },
          footer: {
            className: 'relative',
          },
          toolbar: {
            loading,
            title: 'Scheduled Reports',
          },
        }}
      />
    </>
  );
};

export default ScheduledReportsGrid;
